// liczbaAnimowana.ts — logika COUNT-UP liczb (R5-15, 2026-10-04). Komponent: components/AnimatedNumber.tsx.
//
// Tu leży wyłącznie to, co da się sprawdzić bez Reacta: czas trwania, krzywa, kwantyzacja klatek,
// jeden wspólny zegar klatek i rozbiór już sformatowanej liczby na wartość + formater.

/** Czas liczenia od 0 do wartości. Zakres z zadania: 600–900 ms. */
export const COUNTUP_MS = 800;

/**
 * Bezpiecznik: gdy zegar klatek stoi (okno zminimalizowane, karta w tle, przeglądarka nie rysuje),
 * po `COUNTUP_MS + BEZPIECZNIK_MS` pokazujemy wartość końcową bez dalszego liczenia. Inaczej warstwa
 * klatek zostałaby na „0 zł” — a zero, którego nikt nie zmierzył, to dokładnie to, czego ekran
 * nie może pokazać jako danych. Zmierzone 2026-10-04: w niewidocznym panelu `requestAnimationFrame`
 * nie wywołał się ani razu przez pół sekundy, mimo `visibilityState = 'visible'`.
 */
export const BEZPIECZNIK_MS = 1000;

/** Wyhamowanie (ease-out cubic): szybki start, łagodne dojście do celu. */
export function wyhamowanie(t: number): number {
  const p = Math.min(1, Math.max(0, t));
  return 1 - (1 - p) ** 3;
}

/**
 * Czy wolno animować. Sprawdzane raz, przy wejściu wartości na ekran.
 *
 * `prefers-reduced-motion: reduce` → nie (od razu wartość końcowa). Środowisko bez `matchMedia`
 * albo `requestAnimationFrame` (np. jsdom w testach, stara przeglądarka) traktujemy tak samo —
 * gdy nie wiemy, czy ruch jest pożądany, bezpieczniej go nie robić.
 */
export function ruchDozwolony(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  if (typeof window.requestAnimationFrame !== 'function') return false;
  // Karta w tle: nikt tego nie zobaczy, a zegar klatek i tak stoi — od razu wartość końcowa.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Liczba miejsc po przecinku kroku: 1 → 0, 0.01 → 2, 100 → 0, 1e-7 → 7. */
export function miejscaKroku(krok: number): number {
  if (!Number.isFinite(krok) || krok <= 0) return 0;
  const s = String(krok);
  const e = s.indexOf('e-');
  if (e >= 0) return Number(s.slice(e + 2));
  const kropka = s.indexOf('.');
  return kropka < 0 ? 0 : s.length - kropka - 1;
}

/**
 * Klatka pośrednia skwantowana do kroku i przycięta do przedziału [0, cel].
 *
 * Kwantyzacja to ochrona przed artefaktami precyzji: bez niej klatka „0,1 + 0,2” formatuje się
 * jako 0,30000000000000004, a kwota z groszami przy formaterze pełnych złotych przeskakiwałaby
 * między „1234 zł” i „1234,56 zł”. Zero ujemne zamieniamy na zero („-0,00 zł” nie istnieje).
 */
export function kwantyzuj(x: number, krok: number, cel: number): number {
  const k = Number.isFinite(krok) && krok > 0 ? krok : 1;
  const q = Number((Math.round(x / k) * k).toFixed(miejscaKroku(k)));
  const dol = Math.min(0, cel);
  const gora = Math.max(0, cel);
  const v = Math.min(gora, Math.max(dol, q));
  return v === 0 ? 0 : v;
}

// ── JEDEN ZEGAR KLATEK DLA CAŁEGO EKRANU ────────────────────────────────────────────────────────
//
// Ekran z dziesięcioma liczbami NIE uruchamia dziesięciu pętli. Każda animacja zapisuje się do
// jednego `requestAnimationFrame`; pętla gaśnie sama, gdy nikt już nie liczy.

/** Funkcja klatki: dostaje czas klatki, zwraca `true`, gdy animacja się skończyła. */
export type Klatka = (teraz: number) => boolean;

const aktywne = new Set<Klatka>();
let uchwyt: number | null = null;

function petla(teraz: number): void {
  uchwyt = null;
  for (const klatka of Array.from(aktywne)) {
    if (klatka(teraz)) aktywne.delete(klatka);
  }
  if (aktywne.size > 0) uchwyt = window.requestAnimationFrame(petla);
}

export function zapiszKlatke(klatka: Klatka): void {
  aktywne.add(klatka);
  if (uchwyt === null) uchwyt = window.requestAnimationFrame(petla);
}

export function wypiszKlatke(klatka: Klatka): void {
  aktywne.delete(klatka);
  if (aktywne.size === 0 && uchwyt !== null) {
    window.cancelAnimationFrame(uchwyt);
    uchwyt = null;
  }
}

/** Ile animacji liczy teraz (do testów i diagnostyki). */
export function liczbaAktywnychAnimacji(): number {
  return aktywne.size;
}

// ── ROZBIÓR SFORMATOWANEJ LICZBY ───────────────────────────────────────────────────────────────
//
// Karty KPI dostają wartość JUŻ SFORMATOWANĄ („84 360,00 zł”, „1284”, „69,2%”, „+3,5 p.p.”).
// Żeby animować je bez przepisywania kilkudziesięciu wywołań, rozbieramy tekst na:
// przedrostek + znak + liczbę + przyrostek, i składamy klatki pośrednie TYM SAMYM kształtem:
// ta sama liczba miejsc po przecinku, te same separatory, ten sam znak minus. Klatka końcowa
// to DOKŁADNIE tekst źródłowy, a nie jego rekonstrukcja.
//
// Rozbiór jest celowo ostrożny. Tekst, który nie jest JEDNĄ liczbą (data, godzina, „3 z 5”,
// „W40”, „brak danych”, „—”), zwraca null — wtedy nic się nie animuje i tekst stoi jak był.

/** Twarda spacja, wąska twarda spacja i zwykła spacja — separatory tysięcy. */
const NBSP = String.fromCharCode(0xa0);
const NNBSP = String.fromCharCode(0x202f);
const MINUS = String.fromCharCode(0x2212);
const SEPARATORY = `${NBSP}${NNBSP} `;

const LICZBA_PL = new RegExp(
  `^([\\s\\S]*?)([+\\-${MINUS}]?)(\\d{1,3}(?:[${SEPARATORY}]\\d{3})+|\\d+)(?:,(\\d+))?([\\s\\S]*)$`,
);
const LICZBA_JS = /^(-?)(\d+)(?:\.(\d+))?$/;
const CYFRA = /\d/;
const LITERA_LUB_CYFRA = /[\p{L}\p{N}]/u;

/** Największa wartość, przy której klatki nie tracą precyzji (z zapasem dla groszy). */
const BEZPIECZNY_ZAKRES = 1e13;

export type RozbiorLiczby = {
  /** Wartość liczbowa odczytana z tekstu. */
  wartosc: number;
  /** Krok klatek = najmniejsza widoczna jednostka (1, 0,1, 0,01 …). */
  krok: number;
  /** Formater klatek; dla `wartosc` zwraca dokładnie tekst źródłowy. */
  format: (n: number) => string;
};

function grupuj(cyfry: string, separator: string, odIluCyfr: number): string {
  return cyfry.length >= odIluCyfr ? cyfry.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : cyfry;
}

export function rozbierzLiczbe(tekst: string): RozbiorLiczby | null {
  if (typeof tekst !== 'string' || !CYFRA.test(tekst)) return null;

  const pl = LICZBA_PL.exec(tekst);
  if (pl) {
    const [, przed = '', znak = '', cale = '', ulamek = '', po = ''] = pl;
    if (!CYFRA.test(przed) && !CYFRA.test(po) && poprawneSasiedztwo(przed, po)) {
      const separator = Array.from(cale).find((z) => SEPARATORY.includes(z)) ?? null;
      const cyfry = separator ? cale.split(separator).join('') : cale;
      if (cyfry.length > 1 && cyfry.startsWith('0')) return null; // „05” to kawałek daty lub godziny
      // pl-PL (Intl) grupuje od pięciu cyfr; tekst z separatorem przy czterech cyfrach grupuje od czterech.
      // Tekst bez separatora przy pięciu i więcej cyfrach nie grupuje wcale.
      const odIlu = separator ? (cyfry.length === 4 ? 4 : 5) : cyfry.length >= 5 ? Infinity : 5;
      return zbuduj(tekst, {
        przed,
        po,
        znak,
        cyfry,
        ulamek,
        przecinek: ',',
        separator: separator ?? NBSP,
        odIlu,
      });
    }
  }

  // Liczba wypisana wprost przez JS (np. `String(69.2)` → „69.2”): kropka dziesiętna, bez grup.
  const js = LICZBA_JS.exec(tekst);
  if (js) {
    const [, znak = '', cyfry = '', ulamek = ''] = js;
    if (cyfry.length > 1 && cyfry.startsWith('0')) return null;
    return zbuduj(tekst, { przed: '', po: '', znak, cyfry, ulamek, przecinek: '.', separator: '', odIlu: Infinity });
  }
  return null;
}

/** Liczba nie może być doklejona do litery ani cyfry („W40”, „3D”) — to identyfikator, nie miara. */
function poprawneSasiedztwo(przed: string, po: string): boolean {
  const ostatni = przed.slice(-1);
  const pierwszy = po.slice(0, 1);
  return !(ostatni && LITERA_LUB_CYFRA.test(ostatni)) && !(pierwszy && LITERA_LUB_CYFRA.test(pierwszy));
}

function zbuduj(
  tekst: string,
  c: {
    przed: string;
    po: string;
    znak: string;
    cyfry: string;
    ulamek: string;
    przecinek: string;
    separator: string;
    odIlu: number;
  },
): RozbiorLiczby | null {
  const miejsca = c.ulamek.length;
  const ujemna = c.znak === '-' || c.znak === MINUS;
  const modul = Number(`${c.cyfry}${miejsca ? `.${c.ulamek}` : ''}`);
  if (!Number.isFinite(modul) || modul >= BEZPIECZNY_ZAKRES) return null;
  const wartosc = ujemna ? -modul : modul;
  const krok = miejsca ? Number(`1e-${miejsca}`) : 1;

  const format = (n: number): string => {
    if (n === wartosc) return tekst;
    const [calkowite = '0', dziesietne = ''] = Math.abs(n).toFixed(miejsca).split('.');
    const liczba = `${grupuj(calkowite, c.separator, c.odIlu)}${miejsca ? `${c.przecinek}${dziesietne}` : ''}`;
    // Widoczne zero nie ma znaku: ani „-0,00 zł”, ani „+0 p.p.”.
    const zero = !/[1-9]/.test(liczba);
    const znak = zero ? '' : n < 0 ? (c.znak === MINUS ? MINUS : '-') : c.znak === '+' ? '+' : '';
    return `${c.przed}${znak}${liczba}${c.po}`;
  };
  return { wartosc, krok, format };
}
